import { Alert, Badge, Button, Card, PageHeader } from '@safepos/ui';
import { useState } from 'react';
import { download, upload } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useSubmit } from '../lib/useForm';
import { useInvalidate } from './CatalogPages';

interface Result {
  dryRun: boolean;
  totalRows: number;
  created: number;
  updated: number;
  skippedRows: number;
  newCategories: number;
  errors: { row: number; message: string }[];
  errorsTruncated: boolean;
}

export function ImportPage() {
  const { can } = useAuth();
  const inv = useInvalidate();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const run = useSubmit(async (dry: boolean) => {
    setResult(await upload<Result>('tenant', `/catalog/import${dry ? '?dryRun=1' : ''}`, file!));
    if (!dry) inv('products', 'categories', 'catalog-summary');
  });
  const exp = useSubmit(() => download('tenant', '/catalog/export', 'produk.csv'));
  const template =
    'name,category,variant,sku,barcode,price,cost,unit,taxable,active,description\nKopi Susu,Minuman,,KS-01,,25000,10000,gelas,ya,ya,\nKaos,Pakaian,S,KS-S,,80000,40000,pcs,ya,ya,\nKaos,Pakaian,M,KS-M,,80000,40000,pcs,ya,ya,\n';
  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob(['﻿' + template], { type: 'text/csv' }));
    Object.assign(document.createElement('a'), {
      href: url,
      download: 'templat-produk.csv',
    }).click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <>
      <PageHeader
        title="Impor & Ekspor Produk"
        subtitle="Kelola banyak produk sekaligus lewat CSV (Excel/Google Sheets)"
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Impor dari CSV">
          {!can('catalog:write') ? (
            <Alert tone="info">Anda tidak punya izin mengimpor.</Alert>
          ) : (
            <div className="space-y-4">
              {run.error && <Alert>{run.error}</Alert>}
              <p className="text-sm text-slate-600">
                Kolom:{' '}
                <code>
                  name, category, variant, sku, barcode, price, cost, unit, taxable, active,
                  description
                </code>{' '}
                (judul Indonesia seperti <code>nama, kategori, harga</code> juga dikenali). Produk
                dicocokkan lewat SKU; yang sudah ada diperbarui, yang baru dibuat. Maks. 10.000
                baris.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  aria-label="Berkas CSV"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    setResult(null);
                  }}
                  className="text-sm"
                />
                <Button variant="ghost" onClick={downloadTemplate}>
                  Unduh templat
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={!file || run.busy}
                  onClick={() => void run.run(true)}
                >
                  Periksa dulu
                </Button>
                <Button disabled={!file || run.busy} onClick={() => void run.run(false)}>
                  {run.busy ? 'Memproses…' : 'Impor sekarang'}
                </Button>
              </div>
              {result && (
                <div className="space-y-3">
                  <Alert tone={result.skippedRows ? 'info' : 'success'}>
                    {result.dryRun
                      ? 'Hasil pemeriksaan (belum ada data yang diubah): '
                      : 'Impor selesai: '}
                    {result.created} produk baru, {result.updated} diperbarui,{' '}
                    {result.newCategories} kategori baru, {result.skippedRows} baris dilewati dari{' '}
                    {result.totalRows} baris.
                  </Alert>
                  {result.errors.length > 0 && (
                    <div className="max-h-56 overflow-auto rounded-lg border border-slate-200">
                      <table className="w-full text-left text-sm">
                        <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Baris</th>
                            <th className="px-3 py-2">Masalah</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {result.errors.map((e, i) => (
                            <tr key={i}>
                              <td className="px-3 py-1">
                                <Badge tone="warning">{e.row}</Badge>
                              </td>
                              <td className="px-3 py-1">{e.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.errorsTruncated && (
                        <p className="p-2 text-xs text-slate-500">
                          Menampilkan 200 masalah pertama.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>
        <Card title="Ekspor ke CSV">
          <div className="space-y-4">
            {exp.error && <Alert>{exp.error}</Alert>}
            <p className="text-sm text-slate-600">
              Unduh seluruh katalog (satu baris per varian). Berkas ini bisa diedit lalu diimpor
              kembali.
            </p>
            <Button onClick={() => void exp.run()} disabled={exp.busy}>
              Unduh CSV
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}
